import { useState, useEffect, useMemo, useRef, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  FileText,
  AlertCircle,
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import SortableTableHeader from '../../components/SortableTableHeader';
import TransactionListTotalsBar from '../../components/transactions/TransactionListTotalsBar';
import RowActionsMenu from '../../components/transactions/RowActionsMenu';
import PurchaseOrderSendModal from '../../components/PurchaseOrderSendModal';
import {
  getPurchaseOrders,
  deletePurchaseOrder,
  downloadPurchaseOrderPdf,
} from '../../utils/purchase/purchaseOrderApi';
import { printPurchaseOrder } from '../../utils/purchase/printPurchaseOrder';
import {
  getPurchaseOrderVendorName,
  type PurchaseOrder,
  type PurchaseOrderStatus,
  type GetPurchaseOrdersParams,
} from '../../types/purchase/purchaseOrder';
import type { PaginationMeta } from '../../types/journalEntry';
import { formatCurrency } from '../../utils/formatCurrency';
import { toast, toastRouteSuccess, clearRouteState } from '../../utils/toast';
import { todayLocalIso, daysAgoLocalIso } from '../../utils/localDate';
import { buildPurchaseOrderRowActions } from '../../utils/purchaseOrderRowActions';
import {
  computePurchaseOrderTotals,
  purchaseOrderRowKey,
} from '../../utils/purchaseOrderSelection';

type SortField = 'po_number' | 'po_date' | 'total_amount' | 'status';
type SortDir = 'asc' | 'desc';

type StatusFilter = 'all' | PurchaseOrderStatus;

const STATUS_TABS: { id: StatusFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'DRAFT', label: 'Draft' },
  { id: 'SENT', label: 'Sent' },
  { id: 'ACKNOWLEDGED', label: 'Acknowledged' },
  { id: 'CLOSED', label: 'Closed' },
  { id: 'CANCELLED', label: 'Cancelled' },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();

const getStatusColor = (status: string) => {
  switch (status.toUpperCase()) {
    case 'DRAFT':
      return 'bg-gray-100 text-gray-700';
    case 'SENT':
      return 'bg-blue-100 text-blue-800';
    case 'ACKNOWLEDGED':
      return 'bg-green-100 text-green-800';
    case 'CLOSED':
      return 'bg-slate-100 text-slate-800';
    case 'CANCELLED':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const canManagePurchaseOrders = () => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

const canDeletePurchaseOrders = () =>
  localStorage.getItem('user_role')?.toUpperCase() === 'ADMIN';

const defaultSortDir = (field: SortField): SortDir =>
  field === 'po_date' || field === 'total_amount' ? 'desc' : 'asc';

const toApiSort = (field: SortField, dir: SortDir): string =>
  dir === 'desc' ? `-${field}` : field;

const openPurchaseOrder = (id: number) => `/purchase-orders/manage?id=${id}`;

const PurchaseOrders = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendPurchaseOrderRow, setSendPurchaseOrderRow] = useState<PurchaseOrder | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [startDate, setStartDate] = useState(() => daysAgoLocalIso(90));
  const [endDate, setEndDate] = useState(() => todayLocalIso());
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(25);
  const [sortField, setSortField] = useState<SortField>('po_date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const state = location.state as { successMessage?: string } | null;
    toastRouteSuccess(state);
    if (state?.successMessage) {
      clearRouteState();
    }
  }, [location.state]);

  const fetchPurchaseOrders = async (pageNum = page) => {
    try {
      setLoading(true);
      setError(null);

      const params: GetPurchaseOrdersParams = {
        sort: toApiSort(sortField, sortDir),
        per_page: perPage,
        page: pageNum,
      };
      if (searchQuery) params.search = searchQuery;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (startDate) params.po_date_from = startDate;
      if (endDate) params.po_date_to = endDate;

      const result = await getPurchaseOrders(params);
      setPurchaseOrders(result.purchaseOrders);
      setMeta(result.meta);
      setPage(result.meta.current_page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch purchase orders');
      setPurchaseOrders([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchaseOrders(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, startDate, endDate, searchQuery, perPage, sortField, sortDir]);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    setSearchQuery(searchInput.trim());
    setPage(1);
  };

  const handleSort = (column: string) => {
    const col = column as SortField;
    if (sortField === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(col);
      setSortDir(defaultSortDir(col));
    }
    setPage(1);
  };

  const handleDelete = async (po: PurchaseOrder) => {
    if (!canDeletePurchaseOrders()) return;
    if (!window.confirm(`Delete purchase order ${po.po_number}? This cannot be undone.`)) return;

    try {
      setActionLoading(po.id);
      await deletePurchaseOrder(po.id);
      toast.success(`Purchase order ${po.po_number} deleted.`);
      await fetchPurchaseOrders(page);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete purchase order');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePrint = async (po: PurchaseOrder) => {
    try {
      setActionLoading(po.id);
      await printPurchaseOrder(po);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to print purchase order');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadPdf = async (po: PurchaseOrder) => {
    try {
      setActionLoading(po.id);
      await downloadPurchaseOrderPdf(po.id, `${po.po_number}.pdf`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to download purchase order PDF');
    } finally {
      setActionLoading(null);
    }
  };

  const openSendModal = (po: PurchaseOrder) => {
    if (!canManagePurchaseOrders()) return;
    setSendPurchaseOrderRow(po);
    setShowSendModal(true);
  };

  const handleSent = (updated: PurchaseOrder) => {
    setPurchaseOrders((prev) =>
      prev.map((row) => (row.id === updated.id ? { ...row, ...updated } : row))
    );
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setStatusFilter('all');
    setStartDate(daysAgoLocalIso(90));
    setEndDate(todayLocalIso());
    setPage(1);
  };

  const hasActiveFilters =
    Boolean(searchQuery) ||
    statusFilter !== 'all' ||
    startDate !== daysAgoLocalIso(90) ||
    endDate !== todayLocalIso();

  const columnTotals = useMemo(
    () => computePurchaseOrderTotals(purchaseOrders),
    [purchaseOrders]
  );

  const visibleRowKeys = useMemo(
    () => purchaseOrders.map(purchaseOrderRowKey),
    [purchaseOrders]
  );

  const selectedRows = useMemo(
    () => purchaseOrders.filter((po) => selectedKeys.has(purchaseOrderRowKey(po))),
    [purchaseOrders, selectedKeys]
  );

  const selectedTotals = useMemo(
    () => computePurchaseOrderTotals(selectedRows),
    [selectedRows]
  );

  const allPageSelected =
    visibleRowKeys.length > 0 && visibleRowKeys.every((key) => selectedKeys.has(key));

  const somePageSelected = visibleRowKeys.some((key) => selectedKeys.has(key));

  const toggleRow = (key: string, checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const toggleSelectAll = (checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        for (const key of visibleRowKeys) {
          next.add(key);
        }
      } else {
        for (const key of visibleRowKeys) {
          next.delete(key);
        }
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedKeys(new Set());

  useEffect(() => {
    setSelectedKeys(new Set());
  }, [statusFilter, startDate, endDate, searchQuery, page, perPage]);

  useEffect(() => {
    const visibleKeySet = new Set(visibleRowKeys);
    setSelectedKeys((prev) => {
      const pruned = new Set([...prev].filter((key) => visibleKeySet.has(key)));
      return pruned.size === prev.size ? prev : pruned;
    });
  }, [visibleRowKeys]);

  useEffect(() => {
    const input = selectAllCheckboxRef.current;
    if (input) {
      input.indeterminate = somePageSelected && !allPageSelected;
    }
  }, [somePageSelected, allPageSelected]);

  const totalsBarMode = selectedTotals.count > 0 ? 'selected' : 'page';
  const totalsBarData = totalsBarMode === 'selected' ? selectedTotals : columnTotals;
  const totalsBarPageSuffix =
    meta && meta.total > columnTotals.count ? ' on this page' : '';
  const showTotalsBar = purchaseOrders.length > 0;

  if (loading && purchaseOrders.length === 0) {
    return (
      <div className="h-screen bg-gray-100 flex overflow-hidden w-full">
        <Sidebar />
        <main className="flex-1 flex flex-col min-h-0 overflow-hidden p-4 md:p-6 relative">
          <div className="flex flex-1 items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-100 flex overflow-hidden w-full">
      <Sidebar />
      <main className="flex-1 flex flex-col min-h-0 overflow-hidden p-4 md:p-6 relative">
        <div className="flex flex-1 flex-col min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="w-full max-w-full">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-gray-900">Purchase Orders</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Informational POs — no ledger, inventory, or bill impact
                </p>
              </div>
              {canManagePurchaseOrders() && (
                <button
                  type="button"
                  onClick={() => navigate('/purchase-orders/manage')}
                  className="flex items-center justify-center px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  New Purchase Order
                </button>
              )}
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex gap-2 text-sm">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span className="flex-1">{error}</span>
              <button onClick={() => fetchPurchaseOrders(page)} className="underline shrink-0">
                Retry
              </button>
            </div>
          )}

          <div className="mb-4 flex flex-wrap gap-2">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setStatusFilter(tab.id);
                  setPage(1);
                }}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  statusFilter === tab.id
                    ? 'bg-slate-800 text-white shadow-sm'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <form onSubmit={handleSearch} className="mb-6 bg-white p-4 rounded-lg shadow">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    placeholder="PO #, vendor, memo..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-800"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PO date from</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setPage(1);
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PO date to</label>
                <input
                  type="date"
                  value={endDate}
                  min={startDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setPage(1);
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-lg hover:bg-slate-700"
              >
                Search
              </button>
              {hasActiveFilters && (
                <>
                  <Filter className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-500">
                    {meta?.total ?? purchaseOrders.length} purchase orders
                  </span>
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="text-sm text-slate-700 underline"
                  >
                    Clear
                  </button>
                </>
              )}
            </div>
          </form>

          <div className="bg-white rounded-lg shadow overflow-x-auto">
            {loading ? (
              <div className="p-12 flex justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
              </div>
            ) : (
              <table className="w-full min-w-[800px] border-separate border-spacing-0">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <input
                        ref={selectAllCheckboxRef}
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={(e) => toggleSelectAll(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-slate-800"
                        aria-label="Select all on this page"
                      />
                    </th>
                    <SortableTableHeader
                      label="Date"
                      column="po_date"
                      currentSort={sortField}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortableTableHeader
                      label="PO #"
                      column="po_number"
                      currentSort={sortField}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-900 bg-gray-50 uppercase tracking-wide shadow-[inset_0_-1px_0_0_rgb(229,231,235)]">
                      Vendor
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-900 bg-gray-50 uppercase tracking-wide shadow-[inset_0_-1px_0_0_rgb(229,231,235)]">
                      Expected Delivery
                    </th>
                    <SortableTableHeader
                      label="Total"
                      column="total_amount"
                      currentSort={sortField}
                      sortDir={sortDir}
                      onSort={handleSort}
                      align="right"
                    />
                    <SortableTableHeader
                      label="Status"
                      column="status"
                      currentSort={sortField}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <th className="px-4 py-3 text-center text-sm font-bold text-gray-900 bg-gray-50 uppercase tracking-wide sticky top-0 z-20 shadow-[inset_0_-1px_0_0_rgb(229,231,235)]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {purchaseOrders.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center">
                        <FileText className="mx-auto h-10 w-10 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500">No purchase orders found.</p>
                        {canManagePurchaseOrders() && (
                          <button
                            type="button"
                            onClick={() => navigate('/purchase-orders/manage')}
                            className="mt-3 text-sm text-slate-700 underline"
                          >
                            Create the first purchase order
                          </button>
                        )}
                      </td>
                    </tr>
                  ) : (
                    purchaseOrders.map((po) => {
                      const busy = actionLoading === po.id;
                      const rowKey = purchaseOrderRowKey(po);
                      const isCheckboxSelected = selectedKeys.has(rowKey);
                      return (
                        <tr
                          key={rowKey}
                          className={`hover:bg-gray-50 cursor-pointer${
                            isCheckboxSelected ? ' bg-slate-50/60' : ''
                          }`}
                          onClick={() => navigate(openPurchaseOrder(po.id))}
                        >
                          <td className="px-4 py-4 w-10" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isCheckboxSelected}
                              onChange={(e) => toggleRow(rowKey, e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 text-slate-800"
                              aria-label={`Select ${po.po_number}`}
                            />
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {formatDate(po.po_date)}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            {po.po_number}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {getPurchaseOrderVendorName(po)}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {po.expected_delivery_date
                              ? formatDate(po.expected_delivery_date)
                              : '—'}
                          </td>
                          <td className="px-6 py-4 text-sm text-right font-medium text-gray-900">
                            {formatCurrency(po.total_amount)}
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(po.status)}`}
                            >
                              {formatLabel(po.status)}
                            </span>
                          </td>
                          <td
                            className="px-4 py-4 text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <RowActionsMenu
                              {...buildPurchaseOrderRowActions(po, {
                                onOpen: () => navigate(openPurchaseOrder(po.id)),
                                onPrint: () => void handlePrint(po),
                                onDownloadPdf: () => void handleDownloadPdf(po),
                                onSend: () => openSendModal(po),
                                onDelete: () => void handleDelete(po),
                              })}
                              disabled={busy}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>

          {meta && (
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <label htmlFor="purchase-orders-per-page" className="text-gray-700">
                  Rows per page
                </label>
                <select
                  id="purchase-orders-per-page"
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                  className="px-2 py-1.5 border border-gray-300 rounded-lg bg-white text-sm"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
              <span>
                Page {meta.current_page} of {meta.last_page} · {meta.total} total
              </span>
              {meta.last_page > 1 && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => fetchPurchaseOrders(meta.current_page - 1)}
                    disabled={meta.current_page <= 1 || loading}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => fetchPurchaseOrders(meta.current_page + 1)}
                    disabled={meta.current_page >= meta.last_page || loading}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
          </div>

          {showTotalsBar && !loading && (
            <TransactionListTotalsBar
              mode={totalsBarMode}
              count={totalsBarData.count}
              totalAmount={totalsBarData.totalAmount}
              balance={0}
              hideBalance
              pageCountSuffix={totalsBarPageSuffix}
              onClear={totalsBarMode === 'selected' ? clearSelection : undefined}
            />
          )}
        </div>
      </main>

      {sendPurchaseOrderRow && (
        <PurchaseOrderSendModal
          purchaseOrder={sendPurchaseOrderRow}
          isOpen={showSendModal}
          onClose={() => {
            setShowSendModal(false);
            setSendPurchaseOrderRow(null);
          }}
          onSent={handleSent}
        />
      )}
    </div>
  );
};

export default PurchaseOrders;
